import React from 'react';
import { useAppDispatch } from '@/hooks';
import {
  clearNewPatientFormValues,
  closeNewPatientDrawer,
  useCurrentHospitalCountry
} from '@/modules/appContext';
import {
  DirectPatientRequest,
  useCreateDirectPatientMutation
} from '@/modules/pendingPatient';
import { useTranslator, TranslatedText } from '@ianesthesie-kit/core';
import { Box, Collapse, Grid, TextField } from '@mui/material';
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  FormikProps
} from 'formik';
import {
  BirthDateFormDatePicker,
  FormInput,
  FormTelInput,
  GenderFormToggle,
  UsernameFormInput
} from '@ianesthesie-kit/form';
import isEmpty from 'lodash/fp/isEmpty';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import {
  DirectPatientSchema,
  directPatientValidation,
  getPatientInitialFormValues,
  isTelValidCandidate,
  isValidTel
} from './patient.schema';
import { SurgeryDateField } from './formComponents';
import { Gender } from '@ianesthesie-kit/services';
import { setNewPatientSnackbar } from '@/modules/ui';
import { SubmitButton } from '@/components/drawer/components/SubmitButton';
import { AppConfigService } from '@/config';
import { ReduxFormValuesListener } from '@/components/drawer/newPatientDrawer/ReduxFormValuesListener';
import { RegionAutocomplete } from '@/components/regionAutocomplete';
import { FormToggleButton } from '@/components/formToggleButton';

const cleanFormValues = (values: DirectPatientSchema) =>
  Object.entries(values)
    .filter(([, v]) => typeof v === 'boolean' || !!v)
    .reduce(
      (acc, [k, v]) => ({
        ...acc,
        [k]: v
      }),
      {}
    );

export const DirectPatientForm = () => {
  const translate = useTranslator();
  const dispatch = useAppDispatch();
  const [createDirectPatient, { isLoading }] = useCreateDirectPatientMutation();
  const { showErrorDialog } = useErrorDialog();

  const closeDrawer = () => {
    dispatch(closeNewPatientDrawer());
  };

  async function onSubmit(
    rawValues: DirectPatientSchema,
    helper: FormikHelpers<DirectPatientSchema>
  ) {
    if (rawValues.gender != Gender.FEMALE) {
      rawValues.pregnant = undefined;
      rawValues.maidenName = undefined;
    }
    const values = cleanFormValues(rawValues);
    createDirectPatient(values as DirectPatientRequest)
      .unwrap()
      .then((resp) => {
        closeDrawer();
        setTimeout(() => {
          helper.resetForm();
          dispatch(clearNewPatientFormValues());
          dispatch(setNewPatientSnackbar(resp.data));
        });
      })
      .catch(showErrorDialog);
  }

  const country =
    useCurrentHospitalCountry() ??
    AppConfigService.getInstance().countryBasedOnEnv;

  const showAddressDetails = country === 'US';

  const canSubmit = (formik: FormikProps<DirectPatientSchema>) => {
    const can =
      !isLoading &&
      formik.isValid &&
      !formik.isSubmitting &&
      !isEmpty(formik.touched);
    if (!can) {
      return false;
    }
    return isValidTel(formik.values.phoneNumber);
  };

  return (
    <Formik
      validationSchema={directPatientValidation}
      initialValues={getPatientInitialFormValues('patient', showAddressDetails)}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <>
          <ReduxFormValuesListener />
          <Box sx={{ mt: 1 }}>
            <Form>
              <FormInput
                fullWidth
                margin="normal"
                label={translate('app.user.firstName')}
                name="firstName"
                autoComplete="off"
              />

              <FormInput
                fullWidth
                margin="normal"
                label={translate('app.user.lastName')}
                name="lastName"
                autoComplete="off"
              />

              {country === 'US' && (
                <FormInput
                  fullWidth
                  margin="normal"
                  label={translate('app.user.middleName')}
                  name="middleName"
                  autoComplete="off"
                />
              )}

              <Collapse in={formik.values.gender === Gender.FEMALE}>
                <FormInput
                  fullWidth
                  margin="normal"
                  label={translate('app.user.maidenName')}
                  name="maidenName"
                  autoComplete="off"
                />
              </Collapse>

              <BirthDateFormDatePicker applyAgeLimits={false} />

              <GenderFormToggle />

              <Field
                name="email"
                render={({ field }: FieldProps) => (
                  <TextField
                    fullWidth
                    margin="normal"
                    label={translate('app.user.email')}
                    autoComplete="email"
                    error={!!formik.errors.email && !!formik.touched.email}
                    helperText={
                      formik.errors.email &&
                      formik.touched.email &&
                      translate(formik.errors.email)
                    }
                    {...field}
                    onChange={(e) => {
                      formik.setFieldValue(field.name, e.target.value);
                    }}
                  />
                )}
              />

              <FormTelInput
                name="phoneNumber"
                fast={false}
                label={translate('app.phoneNumber')}
                defaultCountry={country}
                validatePhoneNumber={isTelValidCandidate(
                  formik.values.phoneNumber
                )}
              />

              {showAddressDetails && (
                <>
                  <FormInput
                    fullWidth
                    margin="normal"
                    label={translate('app.user.address.address')}
                    name="addressDetails.address"
                    autoComplete="street-address"
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <FormInput
                        fullWidth
                        margin="normal"
                        label={translate('app.user.address.city')}
                        name="addressDetails.city"
                        autoComplete="address-level2"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        fullWidth
                        margin="normal"
                        label={translate('app.user.zipCode')}
                        name="addressDetails.zipCode"
                        autoComplete="postal-code"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <RegionAutocomplete
                        usOnly
                        label={translate('app.user.address.region')}
                        // error={
                        //   !!formik.errors.addressDetails?.region &&
                        //   !!formik.touched.addressDetails?.region
                        // }
                        // helperText={
                        //   formik.errors.addressDetails?.region &&
                        //   formik.touched.addressDetails?.region &&
                        //   translate(
                        //     formik.errors.addressDetails.region as string
                        //   )
                        // }
                        value={formik.values.addressDetails?.region}
                        onChange={(region) => {
                          formik.setFieldValue('addressDetails.region', region);
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <FormToggleButton
                name="urgentCareNeeded"
                toggledOnTextId="doctor-dashboard.urgentCareNeeded.on"
                toggledOffTextId="doctor-dashboard.urgentCareNeeded.off"
                toggledOnColor={'riskHigh' as any}
                size="medium"
                fullWidth
              />

              <SurgeryDateField />

              <UsernameFormInput />

              <Collapse in={formik.values.gender === Gender.FEMALE}>
                <FormToggleButton
                  name="pregnant"
                  textId="doctor-dashboard.createNewDirectPatient.isPregnant"
                />
              </Collapse>

              <SubmitButton
                fullWidth
                sx={{ mt: 2, mb: 2, textTransform: 'none' }}
                disabled={!canSubmit(formik)}
              >
                <TranslatedText id="doctor-dashboard.createNewDirectPatient.submitBtn" />
              </SubmitButton>
            </Form>
          </Box>
        </>
      )}
    </Formik>
  );
};
